<template>
  <div class="min-audio-player mt-3 mb-2" :id="id">
    <audio
      :key="src"
      ref="audioRef"
      :preload="preload"
      @loadedmetadata="onLoadedMetadata"
      @durationchange="onDurationChange"
      @timeupdate="onTimeUpdate"
      @play="onPlay"
      @pause="onPause"
      @seeking="onSeeking"
      @seeked="onSeeked"
    >
      <source :src="src" />
    </audio>

    <div
      class="flex items-center -m-3 gap-2 mr-0 md:gap-3 cursor-pointer"
      @click.stop="onContainerClick"
    >
     <span 
        :id="`play-${id}`"
        role="button" 
        aria-label="Play" 
        tabindex="0" 
        class="cursor-pointer -mt-1 w-12 inline-flex items-center justify-center" 
        @click.stop="togglePlay" @keydown.enter.stop="togglePlay" @keydown.space.stop="togglePlay" 
        > 
         <Icon :id="`play-icon-${id}`" :icon="playIcon" class="text-[1.714285rem]" :style="{ color: iconColor }" /> 
     </span>
      <div class="md:block text-gray-700 text-xs w-12 flex-shrink-0">
        <span>{{ formatTime(currentTime) }}</span>
      </div>

      <div
        ref="progressBar"
        class="flex-1 cursor-pointer py-3"
        @click.stop.prevent="onSeek($event)"
        @mousedown.stop.prevent="onSeek($event)"
        @touchstart.stop.prevent="onSeekTouchStart"
        @touchmove.stop.prevent="onSeekTouchMove"
        @touchend.stop.prevent="onSeekTouchEnd"
        role="slider"
        aria-label="Audio progress"
        :aria-valuemin="0"
        :aria-valuemax="duration"
        :aria-valuenow="currentTime"
        tabindex="0"
        @keydown.stop="onSeekKeydown"
      >
        <div
          class="w-full rounded-full overflow-hidden h-[0.3rem] py-0"
          :style="{ backgroundColor: progressColor }"
        >
          <div
            class="h-full rounded-full transition duration-150 ease-linear"
            :class="{ 'filter brightness-110': isSeeking }"
            :style="{ width: `${progress}%`, backgroundColor: completedColor }"
          ></div>
        </div>
      </div>
      <div v-if="!mobile || pageType =='details'" class="text-gray-700 text-xs w-12 flex-shrink-0 text-right hidden lg:block">
        <span>{{ formatTime(remainingTime) }}</span>
      </div>
      <span
        :id="`vol-${id}`"
        role="button"
        aria-label="Volume"
        tabindex="0"
        v-if="!mobile || pageType === 'details'"
        class="cursor-pointer w-8 h-8 flex items-center justify-center hidden lg:block"
        @click.stop="toggleVolume"
        @keydown.enter.stop="toggleVolume"
        @keydown.space.stop="toggleVolume"
        >
        <Icon :id="`vol-icon-${id}`" :icon="volumeIcon" class="text-2xl" :style="{ color: iconColor }" />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import Icon from '~/components/ui/atom/icon.vue'

defineOptions({ name: 'AudioPlayer' })

interface Props {
  src: string
  captureClicks?: boolean
  color?: string
  progressColor?: string
  completedColor?: string
  preload?: 'auto' | 'metadata' | 'none'
  pageType?: string
  id?: string
}
const props = withDefaults(defineProps<Props>(), {
  captureClicks: true,
  color: 'var(--color-white)',
  progressColor: 'var(--color-gray-300)',
  completedColor: 'var(--color-gold-300)',
  preload: undefined,
  pageType:'list',
  id:''
})

const mobile = (() => {
  if (typeof navigator === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent || '')
})()

type AudioCoordinator = { current: HTMLAudioElement | null }
const audioCoordinator: AudioCoordinator = (globalThis as any).__audioCoordinator ??= { current: null }

const audioRef = ref<HTMLAudioElement | null>(null)
const progressBar = ref<HTMLElement | null>(null)
const isPaused = ref(true)
const isMuted = ref(false)
const isSeeking = ref(false)
const currentTime = ref(0)
const duration = ref(0)
let lastVolume = 1

const preload = computed(() => (props.preload || 'metadata'))
const playIcon = computed(() => (isPaused.value ? 'play-circle' : 'pause-circle'))
const volumeIcon = computed(() => (isMuted.value ? 'volume-mute' : 'volume-up'))
const iconColor = computed(() => props.color || 'yellow')
const progressColor = computed(() => props.progressColor || 'var(--color-gold-100)')
const completedColor = computed(() => props.completedColor || 'var(--color-gold-300)')
const pageType = computed(() => props.pageType || 'list')

const progress = computed(() =>
  duration.value > 0 ? Math.max(0, Math.min(100, (currentTime.value / duration.value) * 100)) : 0
)

const remainingTime = computed(() => Math.max(0, duration.value - currentTime.value))

function formatTime(seconds: number) {
  if (!seconds || Number.isNaN(seconds)) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

async function play() {
  const a = audioRef.value
  if (!a) return

  if (audioCoordinator.current && audioCoordinator.current !== a) {
    try { audioCoordinator.current.pause() } catch {}
  }
  audioCoordinator.current = a

  try {
    await a.play()
    isPaused.value = false
  } catch {
    isPaused.value = true
  }
}

function pause() {
  const a = audioRef.value
  if (!a) return
  a.pause()
  if (audioCoordinator.current === a) audioCoordinator.current = null
  isPaused.value = true
}

function togglePlay(e?: Event) {
  const a = audioRef.value
  if (!a) return
  if (a.paused) play()
  else pause()
  filterEvents(e)
}

function toggleVolume(e?: Event) {
  const a = audioRef.value
  if (!a) return
  if (isMuted.value) {
    a.volume = lastVolume
    isMuted.value = false
  } else {
    lastVolume = a.volume || 1
    a.volume = 0
    isMuted.value = true
  }
  filterEvents(e)
}

function onContainerClick() {
  const a = audioRef.value
  if (!a) return
  if (a.paused) play()
  else pause()
}

function seekToClientX(clientX: number) {
  const a = audioRef.value
  const bar = progressBar.value
  if (!a || !bar || duration.value <= 0) return

  const rect = bar.getBoundingClientRect()
  const relativeX = Math.min(Math.max(0, clientX - rect.left), rect.width)
  const seekTime = (relativeX / rect.width) * duration.value

  a.currentTime = seekTime
  currentTime.value = seekTime
}

function onSeek(event: MouseEvent) {
  isSeeking.value = true
  seekToClientX(event.clientX)
  isSeeking.value = false
}

function onSeekTouchStart(ev: TouchEvent) {
  const touch = ev.touches[0]
  if (!touch) return
  isSeeking.value = true
  seekToClientX(touch.clientX)
}

function onSeekTouchMove(ev: TouchEvent) {
  const touch = ev.touches[0]
  if (!touch) return
  seekToClientX(touch.clientX)
}

function onSeekTouchEnd() {
  isSeeking.value = false
}

function onSeekKeydown(e: KeyboardEvent) {
  const a = audioRef.value
  if (!a || duration.value <= 0) return
  const step = 5 // seconds
  if (e.key === 'ArrowRight') {
    const next = Math.min(duration.value, a.currentTime + step)
    a.currentTime = next
    currentTime.value = next
  } else if (e.key === 'ArrowLeft') {
    const prev = Math.max(0, a.currentTime - step)
    a.currentTime = prev
    currentTime.value = prev
  }
}

function onLoadedMetadata(e: Event) {
  const t = e.target as HTMLAudioElement
  duration.value = t.duration || 0
}

function onDurationChange(e: Event) {
  const t = e.target as HTMLAudioElement
  duration.value = t.duration || 0
}

function onTimeUpdate(e: Event) {
  const t = e.target as HTMLAudioElement
  currentTime.value = t.currentTime
}

function onPlay() { isPaused.value = false }
function onPause() { isPaused.value = true }

function onSeeking() {
  isSeeking.value = true
  if (audioRef.value) currentTime.value = audioRef.value.currentTime
}

function onSeeked() {
  isSeeking.value = false
  if (audioRef.value) currentTime.value = audioRef.value.currentTime
}

function filterEvents(e?: Event) {
  if (!e || !props.captureClicks) return
  e.stopPropagation()
  e.preventDefault()
}

watch(() => props.src, () => {
  currentTime.value = 0
  duration.value = 0
  const a = audioRef.value
  if (a) {
    a.pause()
    a.removeAttribute('src')
    a.load()
  }
  isPaused.value = true
})

onBeforeUnmount(() => {
  const a = audioRef.value
  if (!a) return
  if (audioCoordinator.current === a) audioCoordinator.current = null
  a.pause()
  a.removeAttribute('src')
  a.load()
})

</script>
